/**
 * SwapInformantsFlow
 * ==================
 *
 * A simple confirmation flow for the "Switch informants" (SWAP) operation.
 * This is used when two informants are assigned to different life stages
 * and the user wants to swap their questionnaire assignments.
 *
 * Unlike the SPLIT flow (which has 4 steps), the SWAP flow is a single
 * review-and-confirm step followed by a success screen — since both
 * informants already exist and no new details need to be entered.
 *
 * The component shows:
 *   - A before/after recap of the swap
 *   - Progress-based warnings for either informant
 *   - A confirmation button
 *   - A success screen after completion
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import BeforeAfterRecap from './BeforeAfterRecap';
import WarningPanel from './WarningPanel';
import ConfirmationScreen from './ConfirmationScreen';
import ButtonSpinner from './ButtonSpinner';

/**
 * @param {Object}   props
 * @param {Object}   props.informantA        – First informant (has one life stage)
 * @param {Object}   props.informantB        – Second informant (has the other life stage)
 * @param {Function} props.onSwap            – (informantAId, informantBId) => Promise
 * @param {Function} props.onBack            – Called when user clicks "Back"
 * @param {boolean}  props.actionLoading     – Whether the backend call is in progress
 * @param {string}   props.actionError       – Error message from the backend
 * @param {Function} props.clearActionState  – Reset actionError/actionSuccess
 * @param {Function} props.getProgressWarning – (informantId) => warning string or null
 * @param {Function} props.onFinish          – Called when user clicks "Finish" on success screen
 * @param {Object}   props.terminology       – { singular, plural } for patient-facing copy
 */
export default function SwapInformantsFlow({
  informantA,
  informantB,
  onSwap,
  onBack,
  actionLoading = false,
  actionError = null,
  clearActionState,
  getProgressWarning,
  onFinish,
  isFinishing = false,
  terminology = { singular: 'Informant', plural: 'Informants' },
}) {
  const [isSuccess, setIsSuccess] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const warningA = getProgressWarning?.(informantA?.id);
  const warningB = getProgressWarning?.(informantB?.id);
  const hasAnyWarning = warningA || warningB;

  // Build before/after data for the recap card.
  // "Before" shows the current assignment; "After" shows the swapped assignment.
  const stagesA = informantA?.lifeStages || [];
  const stagesB = informantB?.lifeStages || [];

  const beforeData = [
    {
      id: informantA?.id,
      name: `${informantA?.firstName} ${informantA?.lastName}`,
      lifeStages: stagesA,
    },
    {
      id: informantB?.id,
      name: `${informantB?.firstName} ${informantB?.lastName}`,
      lifeStages: stagesB,
    },
  ];

  const afterData = [
    {
      id: informantA?.id,
      name: `${informantA?.firstName} ${informantA?.lastName}`,
      lifeStages: stagesB,
    },
    {
      id: informantB?.id,
      name: `${informantB?.firstName} ${informantB?.lastName}`,
      lifeStages: stagesA,
    },
  ];

  const handleConfirmSwap = useCallback(async () => {
    try {
      await onSwap(informantA.id, informantB.id);
      if (mountedRef.current) setIsSuccess(true);
    } catch {
      // Error state managed by the hook via actionError
    }
  }, [onSwap, informantA, informantB]);

  if (isSuccess) {
    return (
      <ConfirmationScreen
        headline="Questionnaires switched successfully!"
        detail={`${informantA?.firstName} now answers the ${stagesB.join(' & ').toLowerCase()} questionnaire, and ${informantB?.firstName} now answers the ${stagesA.join(' & ').toLowerCase()} questionnaire.`}
        onFinish={onFinish}
        isLoading={isFinishing}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Switch {terminology.singular} Questionnaires
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          This will swap which questionnaire each {terminology.singular.toLowerCase()} answers.
        </p>
      </div>

      <BeforeAfterRecap
        before={beforeData}
        after={afterData}
        operationLabel="Switch Questionnaires"
      />

      {/* Progress warnings for each informant */}
      {hasAnyWarning && (
        <WarningPanel variant="warning">
          <p className="font-semibold mb-1">Existing progress will be archived</p>
          <ul className="space-y-1 list-disc pl-4">
            {warningA && (
              <li>{informantA?.firstName}'s progress on their current questionnaire will be archived.</li>
            )}
            {warningB && (
              <li>{informantB?.firstName}'s progress on their current questionnaire will be archived.</li>
            )}
          </ul>
          <p className="mt-1">Both {terminology.plural.toLowerCase()} will start fresh on their new questionnaire.</p>
        </WarningPanel>
      )}

      {!hasAnyWarning && (
        <WarningPanel variant="info">
          Neither {terminology.singular.toLowerCase()} has started their questionnaires yet.
          This switch can be done without any data loss.
        </WarningPanel>
      )}

      {actionError && (
        <WarningPanel variant="error">{actionError}</WarningPanel>
      )}

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={() => { clearActionState?.(); onBack(); }}
          disabled={actionLoading}
          className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleConfirmSwap}
          disabled={actionLoading}
          className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${
            actionLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-violet-600 hover:bg-violet-700'
          }`}
        >
          {actionLoading ? (
            <span className="inline-flex items-center gap-2">
              <ButtonSpinner />
              Switching…
            </span>
          ) : 'Confirm Switch'}
        </button>
      </div>
    </div>
  );
}
